/**
 * Wall reports — session-scoped store for flagged Supporter Wall messages.
 *
 * Per the current phase: reports do NOT persist to a real backend yet.
 * We use `localStorage` so a report placed on a campaign page is visible
 * on the admin dashboard within the same browser (across tabs, across
 * client-side route changes). This is a stand-in for the real
 * `POST /api/wall/messages/:id/report` endpoint.
 *
 * Shape (JSON in localStorage under `mra_wall_reports_v1`):
 * {
 *   [messageId]: {
 *     id, campaignId, campaignName, studentFirstName,
 *     authorName, message, postedAt,
 *     reports: [{ at: ISO, reason: "harassment" | "spam" | "hate" | "other" }],
 *     resolved: null | { at: ISO, action: "approved" | "removed" }
 *   }
 * }
 *
 * TODO(backend):
 *   - POST /api/wall/messages/:id/report { reason }  — client fires this
 *   - GET  /api/admin/reports?status=pending         — admin list view
 *   - POST /api/admin/reports/:id/{approve|remove}   — resolve actions
 */

const KEY = "mra_wall_reports_v1";
const CHANGE_EVENT = "mra:wall-reports-changed";

const safeRead = () => {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(KEY) || "{}");
  } catch (e) {
    return {};
  }
};

const safeWrite = (obj) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(obj));
    // Same-tab listeners never receive the native `storage` event, so we
    // fire a custom one for React components that subscribe below.
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch (e) {
    /* quota / private mode — reports simply won't persist */
  }
};

export const getReports = () => safeRead();

export const isRemoved = (messageId) => {
  const r = safeRead()[messageId];
  return !!(r && r.resolved && r.resolved.action === "removed");
};

export const isFlagged = (messageId) => {
  const r = safeRead()[messageId];
  return !!(r && !r.resolved);
};

export const reportMessage = (messageInfo, reason = "other") => {
  const store = safeRead();
  const existing = store[messageInfo.id];
  const now = new Date().toISOString();
  const nextRecord = existing
    ? {
        ...existing,
        reports: [...existing.reports, { at: now, reason }],
        // Re-open resolved reports if new reports come in after resolution.
        resolved: existing.resolved && existing.resolved.action === "removed"
          ? existing.resolved  // already gone from public view — no-op
          : null,
      }
    : {
        ...messageInfo,
        reports: [{ at: now, reason }],
        resolved: null,
      };
  store[messageInfo.id] = nextRecord;
  safeWrite(store);
  return nextRecord;
};

export const resolveReport = (messageId, action /* "approved" | "removed" */) => {
  const store = safeRead();
  const r = store[messageId];
  if (!r) return null;
  const resolved = { at: new Date().toISOString(), action };
  store[messageId] = { ...r, resolved };
  safeWrite(store);
  return store[messageId];
};

export const deleteReport = (messageId) => {
  const store = safeRead();
  if (!store[messageId]) return;
  delete store[messageId];
  safeWrite(store);
};

// React helper: subscribe to store changes (custom event + storage event).
export const subscribeReports = (callback) => {
  if (typeof window === "undefined") return () => {};
  const same = () => callback(safeRead());
  const cross = (e) => { if (e.key === KEY) callback(safeRead()); };
  window.addEventListener(CHANGE_EVENT, same);
  window.addEventListener("storage", cross);
  return () => {
    window.removeEventListener(CHANGE_EVENT, same);
    window.removeEventListener("storage", cross);
  };
};

export const REPORT_REASONS = [
  { key: "harassment", label: "Harassment or bullying" },
  { key: "hate",       label: "Hate speech" },
  { key: "spam",       label: "Spam or misleading" },
  { key: "other",      label: "Something else" },
];
